import {
  type CarePlanStepBase,
  CarePlanStepStatus,
  StepType,
  isPatientStep,
} from "@/components/care-plan/types";

/**
 * Recursively collect all steps including children nested inside decision
 * step branches. This ensures cycle completion considers branch steps, not
 * just the top-level decision step itself.
 */
export function flattenStepsWithBranchChildren(
  steps: CarePlanStepBase[],
): CarePlanStepBase[] {
  const result: CarePlanStepBase[] = [];
  for (const step of steps) {
    result.push(step);
    if (step.type === StepType.DECISION && step.branches) {
      for (const branch of step.branches) {
        result.push(...flattenStepsWithBranchChildren(branch.steps));
      }
    }
  }
  return result;
}

/**
 * Recursively check whether a step is in a terminal state for cycle
 * completion. A decision step has "infinite depth": it is terminal only
 * when a branch has been chosen AND every step inside that chosen branch
 * is itself terminally complete (recursing into nested decisions).
 *
 * Without this recursion, the decision step's own COMPLETED status (set
 * the moment a branch is picked) would make a cycle pass look done even
 * though the branch's work still has to happen.
 */
export function isStepTerminallyComplete(step: CarePlanStepBase): boolean {
  if (step.type === StepType.DECISION) {
    const selected = (step.branches ?? []).find((b) => !!b.selectedDate);
    if (!selected) return false;
    return selected.steps.every(isStepTerminallyComplete);
  }
  if (!isPatientStep(step)) return true;
  return (
    step.status === CarePlanStepStatus.COMPLETED ||
    step.status === CarePlanStepStatus.SKIPPED
  );
}

/**
 * Group steps belonging to a specific cycle by their pass number.
 * Returns a Map where each key is a pass number and the value is
 * the array of steps for that pass.
 */
export function groupStepsByCyclePass(
  steps: CarePlanStepBase[],
  cycleId: string,
): Map<number, CarePlanStepBase[]> {
  const grouped = new Map<number, CarePlanStepBase[]>();

  for (const step of steps) {
    if (step.cycleId !== cycleId || step.cyclePass == null) continue;
    const existing = grouped.get(step.cyclePass);
    if (existing) {
      existing.push(step);
    } else {
      grouped.set(step.cyclePass, [step]);
    }
  }

  return grouped;
}

export type CyclePassStatus =
  | "completed"
  | "in_progress"
  | "pending"
  | "skipped";

/**
 * Derive an aggregate status for a set of steps within a single cycle pass.
 *
 * - "completed" - every step is COMPLETED
 * - "skipped"   - every step is SKIPPED
 * - "in_progress" - at least one step is COMPLETED or SCHEDULED but not all done
 * - "pending"   - all steps are PENDING (none started)
 */
export function deriveCyclePassStatus(
  steps: CarePlanStepBase[],
): CyclePassStatus {
  if (steps.length === 0) return "pending";

  const statuses = steps
    .filter(
      (s): s is CarePlanStepBase & { status: CarePlanStepStatus } =>
        "status" in s,
    )
    .map((s) => s.status);

  if (statuses.length === 0) return "pending";

  const allCompleted = statuses.every(
    (s) => s === CarePlanStepStatus.COMPLETED,
  );
  if (allCompleted) return "completed";

  const allSkipped = statuses.every((s) => s === CarePlanStepStatus.SKIPPED);
  if (allSkipped) return "skipped";

  const hasProgress = statuses.some(
    (s) =>
      s === CarePlanStepStatus.COMPLETED || s === CarePlanStepStatus.SCHEDULED,
  );
  if (hasProgress) return "in_progress";

  return "pending";
}
